import enUS from 'date-fns/locale/en-US';
import frCA from 'date-fns/locale/fr-CA';
import es from 'date-fns/locale/es';
import setDefaultOptions from 'date-fns/setDefaultOptions';
import { en_US, fr_FR, es_ES } from 'ng-zorro-antd/i18n';
import '@angular/localize/init';
import { registerLocaleData } from '@angular/common';
import { moment } from './moment';

const MomentLocales = {
  'es': 'es',
  'es-MX': 'es-us',
  'fr-CA': 'fr-ca',
};

const DatefnsLocale = {
  'es': es,
  'en': enUS,
  'es-MX': es,
  'fr-CA': frCA,
};

export enum LOCALES {
  FRENCH = 'fr-CA',
  SPANISH = 'es-MX',
  ENGLISH = 'en-US',
}

let registerLocalePromise: Promise<void> = Promise.resolve();
let locale: string = '';

function registerLocale(localeId: string): Promise<void> {
  registerLocalePromise = Promise.all([
    import(
      /* webpackInclude: /(fr-CA|es|es-MX)\.js$/ */
      `@angular/common/locales/${localeId}`),
    import(
      /* webpackInclude: /(fr-ca|es|es-us)\.js$/ */
      `moment/locale/${MomentLocales[localeId]}`),
  ])
    .then(([module]) => {
      registerLocaleData(module.default);
      moment.locale(MomentLocales[localeId]);
      setDefaultOptions({ locale: DatefnsLocale[localeId] });
    });

  return registerLocalePromise;
}

export function getRegisterLocalePromise(): Promise<void> {
  return registerLocalePromise;
}

export function getLocale(): string {
  let l = '';

  if (locale) return locale; // Use cached value if present. locale variable is declared on top of this file
  else if ($localize.locale) return $localize.locale;
  else if (typeof URLSearchParams === 'function') { // Detect locale from query string for JIT build
    const urlParams = new URLSearchParams(window.location.search);
    l = urlParams.get('locale');
  }

  return l || 'en-US';
}

export function getNzLocale() {
  switch (getLocale()) {
  case 'fr-CA':
    return fr_FR;
  case 'es-MX':
    return es_ES;
  default:
    return en_US;
  }
}

export function getNzDateLocale() {
  switch (getLocale()) {
  case 'fr-CA':
    return frCA;
  case 'es-MX':
    return es;
  default:
    return enUS;
  }
}

export function isLocaleFrench() {
  return (getLocale() === 'fr-CA');
}

export function isLocaleEnglish() {
  return (getLocale() === 'en-US');
}

export function isLocaleSpanish() {
  return (getLocale() === 'es-MX');
}

locale = getLocale();
if (locale === 'fr-CA') {
  registerLocale(locale);
} else if (locale === 'es-MX') {
  registerLocale(locale);
} else {
  // reset to default english locale
  moment.locale('en-us');
  setDefaultOptions({ locale: enUS });
}

export function getLocaleForAdaChatbot(): string {
  switch (getLocale()) {
  case 'fr':
  case 'fr-CA':
    return 'fr';
  case 'es':
  case 'es-MX':
    return 'es';
  default:
    return 'en';
  }
}

export function getHelpCenterLocale(): string {
  switch (getLocale()) {
  case 'fr-CA':
    return 'fr-ca';
  case 'es-MX':
    return 'es-mx';
  default:
    return 'en-us';
  }
}

export function getThoughtSpotLocale(): string {
  switch (getLocale()) {
  case 'fr-CA':
    return 'fr-CA';
  case 'es-MX':
    return 'es-ES';
  case 'en-US':
  default:
    return 'en-US';
  }
}

// Bulk edits: Download Excel file API requires language param with these values
export function getLocaleForBulkEdits(): 'fr' | 'es' | 'en' {
  switch (getLocale()) {
  case 'fr-CA':
    return 'fr';
  case 'es-MX':
    return 'es';
  default:
    return 'en';
  }
}
